import { useEffect } from 'react';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Food } from '@/types/food';
import { getFoodThumbnail } from '@/utils/foodThumbnails';

interface DatabaseFood {
  id: string;
  name: string;
  image_url: string | null;
  min_age: number;
  cutting_method: string;
  consistency: string;
  allergy_risk: 'low' | 'medium' | 'high';
  choking_risk: 'low' | 'medium' | 'high';
  allergens: string[];
  preparation: string;
  category: string;
  nutritional_info: any;
  source_url: string | null;
  created_at: string;
  updated_at: string;
  short_description: string | null;
  nutritional_values: any[];
  safety_tips: any[];
  allergen_info: string | null;
  storage_tips: string | null;
  food_combinations: string | null;
  age_preparations: any[];
  is_premium?: boolean | null;
}

const transformFood = (food: DatabaseFood): Food => ({
  id: food.id,
  name: food.name,
  image: food.image_url || getFoodThumbnail(food.name) || '/placeholder.svg',
  minAge: food.min_age,
  cuttingMethod: food.cutting_method,
  consistency: food.consistency,
  allergyRisk: food.allergy_risk,
  chokingRisk: food.choking_risk,
  allergens: food.allergens,
  preparation: food.preparation,
  category: food.category,
  sourceUrl: food.source_url,
  shortDescription: food.short_description,
  nutritionalInfo: food.nutritional_info || {},
  nutritionalValues: food.nutritional_values || [],
  safetyTips: food.safety_tips || [],
  allergenInfo: food.allergen_info,
  storageTips: food.storage_tips,
  foodCombinations: food.food_combinations,
  agePreparations: food.age_preparations || [],
  isPremium: food.is_premium ?? false,
});

/**
 * Entitlement is enforced server-side: the `foods` table has a RESTRICTIVE RLS
 * policy that only returns rows where `is_premium = false` unless the caller is
 * an admin or has an active subscription. This query therefore never receives
 * premium food data for unentitled users — the client gate below is purely UX.
 */
const fetchFoodsFromDB = async (): Promise<Food[]> => {
  const { data, error } = await supabase
    .from('foods')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data as DatabaseFood[]).map(transformFood);
};

export const useFoods = () => {
  const queryClient = useQueryClient();

  const { data: foods = [], isLoading, error } = useQuery({
    queryKey: ['foods'],
    queryFn: fetchFoodsFromDB,
    staleTime: 5 * 60 * 1000,      // Data is fresh for 5 minutes
    gcTime: 30 * 60 * 1000,         // Cache kept for 30 minutes
    refetchOnWindowFocus: false,     // Don't refetch on tab focus
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * Math.pow(2, attempt), 5000),
    placeholderData: keepPreviousData,
  });

  // Realtime subscription — only invalidates cache, never causes loading state
  useEffect(() => {
    let debounceTimer: ReturnType<typeof setTimeout>;

    // Unique channel name per hook instance — a shared name makes Supabase throw
    // "cannot add postgres_changes callbacks after subscribe()" when several
    // components mount useFoods at the same time.
    const channel = supabase
      .channel(`foods-changes-rq-${Math.random().toString(36).slice(2)}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'foods' },
        () => {
          clearTimeout(debounceTimer);
          debounceTimer = setTimeout(() => {
            // Silently refetch in background — React Query keeps previous data visible
            queryClient.invalidateQueries({ queryKey: ['foods'] });
          }, 500);
        }
      )
      .subscribe();

    return () => {
      clearTimeout(debounceTimer);
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  return {
    foods,
    isLoading,
    error: error ? (error instanceof Error ? error.message : 'Failed to fetch foods') : null,
    refetch: () => queryClient.invalidateQueries({ queryKey: ['foods'] }),
  };
};
